/**
 * Checks for what people type into forms. Each returns a short sentence to show beside the field,
 * or null when the value is fine. They are written so a non-technical person knows what to change.
 */

/** The digits of an Indian mobile number, without +91, 91 or a leading 0. */
export function phoneDigits(s: string): string {
  let d = s.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
}

export function phoneProblem(s: string, required = false): string | null {
  const t = s.trim();
  if (!t) return required ? 'Enter a mobile number' : null;
  if (/[^\d\s+\-()]/.test(t)) return 'Use digits only';
  const n = phoneDigits(t).length;
  if (n === 10) return null;
  return n < 10
    ? `A mobile number has 10 digits. You have typed ${n}.`
    : `A mobile number has 10 digits. You have typed ${n}, that is too many.`;
}

export function emailProblem(s: string, required = false): string | null {
  const t = s.trim();
  if (!t) return required ? 'Enter an email address' : null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t)
    ? null
    : 'This does not look like an email address';
}

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

/** What is wrong with a password someone is choosing, or null. */
export function passwordProblem(s: string): string | null {
  if (s.length < PASSWORD_MIN_LENGTH) return `Use at least ${PASSWORD_MIN_LENGTH} characters`;
  if (s.length > PASSWORD_MAX_LENGTH) return `Use ${PASSWORD_MAX_LENGTH} characters at most`;
  if (!s.trim()) return 'A password cannot be only spaces';
  return null;
}

export function nameProblem(s: string, what = 'a name'): string | null {
  const t = s.trim();
  if (!t) return `Enter ${what}`;
  if (t.length < 2) return 'Enter at least 2 letters';
  if (t.length > 120) return 'That is too long (120 letters at most)';
  return null;
}

export function ageProblem(amount: string, unit: 'Y' | 'M' | 'D'): string | null {
  if (!amount.trim()) return 'Enter the age';
  const n = Number(amount);
  if (!Number.isInteger(n) || n < 0) return 'Enter the age as a whole number';
  const max = unit === 'Y' ? 120 : unit === 'M' ? 36 : 60;
  if (n > max) {
    const word = unit === 'Y' ? 'years' : unit === 'M' ? 'months' : 'days';
    return `That is more than ${max} ${word}. Check the age and its unit.`;
  }
  return null;
}

export function birthDateProblem(iso: string, today: Date = new Date()): string | null {
  if (!iso) return 'Choose the date of birth';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 'Enter a real date';
  if (d.getTime() > today.getTime()) return 'The date of birth cannot be in the future';
  if (today.getFullYear() - d.getFullYear() > 120) return 'Check the year';
  return null;
}

/** A rupee amount typed by hand: a number, not negative, at most 2 decimals. */
export function amountProblem(s: string, what = 'amount', required = false): string | null {
  const t = s.trim();
  if (!t) return required ? `Enter the ${what}` : null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return `Enter the ${what} in rupees, for example 500 or 250.50`;
  if (Number(t) > 10_000_000) return 'That amount is too large';
  return null;
}
